import { verifyDocument as verifyDocumentAPI } from 'api/documents'
import { FIELD_TYPE } from 'config'

import { BaseDocument, BaseDocumentJSON } from './BaseDocuments'
import {
  Config,
  ConfigItem,
  Highlight,
  COORDINATE_KEYS,
  Coordinates,
} from './Documents'

type Point = {
  x: number
  y: number
}

type BoundingPoly = {
  vertices: Point[]
}

type SubField = {
  boundingPoly: BoundingPoly
  inferConfidence: number
  inferText: string
  lineBreak: boolean
  name: string
  value: string
  type: string
  valueType: string
}

type Field = {
  subFields: SubField[]
  detectedPage?: number
} & SubField

type ValidationResult = {
  result: string
}

type MatchTemplate = {
  id: number
  name: string
}

type OcrRespBuilder = {
  title: Field
  fields: Field[]
  message: string
  matchTemplate: MatchTemplate
  uuid: string
  name: string
  inferResult: string
  validationResult: ValidationResult
}

type TitleEdit = {
  name: string
  text: string
}

type OcrEditItem = {
  name: string
  text: string
  type: string
  valueType: string
}

type OcrEditResultBuilder = {
  title: TitleEdit
  fields: OcrEditItem[]
}

export type DocumentBuilderJSON = BaseDocumentJSON & {
  ocrResp: OcrRespBuilder
  ocrEditResult: OcrEditResultBuilder
  config: Config
}

const defaultConfig = new Proxy(
  {},
  {
    get(): ConfigItem {
      return {
        display: true,
        edit: true,
        verify: true,
      } as const
    },
  },
)

export class DocumentBuilder extends BaseDocument {
  constructor(public docJSON: DocumentBuilderJSON) {
    super()
    if (!docJSON.config || Object.keys(docJSON.config).length === 0) {
      docJSON.config = defaultConfig
    }
  }
  get isOcrBuilder() {
    return true
  }
  get ocrResp() {
    return this.docJSON.ocrResp
  }
  get ocrEditResult() {
    return this.docJSON.ocrEditResult
  }
  get config() {
    return this.docJSON.config
  }

  async verify(edit: { [x: string]: any }) {
    const doc = this.docJSON.verifyData!.verifyData.map(
      ({ fieldName, user1Data }) =>
        fieldName === 'title'
          ? {
              fieldName: user1Data.name,
              title: {
                name: user1Data.name,
                text: edit._title, // title conflicts with documentDetail
              },
            }
          : {
              fieldName,
              data: {
                ...user1Data,
                checked:
                  user1Data.type === FIELD_TYPE.CHECKBOX
                    ? edit[fieldName]
                    : undefined,
                text:
                  user1Data.type === FIELD_TYPE.CHECKBOX
                    ? undefined
                    : edit[fieldName],
              },
            },
    )
    await verifyDocumentAPI(this.objectID, doc)
  }

  getHighlights() {
    return getHighlights(this.docJSON)
  }
}

const boundingPolyToCoordinates = (
  boundingPoly: BoundingPoly,
): Coordinates | null => {
  if (!boundingPoly?.vertices) {
    return null
  }

  return boundingPoly.vertices.reduce((result, vertex, idx) => {
    const key = COORDINATE_KEYS[idx]
    result[key] = [vertex.x, vertex.y]
    return result
  }, {} as Coordinates)
}

function getHighlights({ ocrResp, config }: DocumentBuilderJSON): Highlight[] {
  const highlights: Highlight[] = []

  const { title } = ocrResp
  if (title) {
    const coordinates = boundingPolyToCoordinates(title.boundingPoly)
    if (coordinates) {
      highlights.push({
        name: title.name,
        label: title.name,
        detectedPage: title.detectedPage || 0,
        coordinates,
      })
    }
  }

  ;(ocrResp?.fields || []).forEach((item) => {
    const coordinates = boundingPolyToCoordinates(item.boundingPoly)
    if (!coordinates || !config[item.name].display) {
      return
    }

    // const isCheckbox = item.type === 'CHECKBOX'
    // const name = `fields[${idx}].${isCheckbox ? 'checked' : 'text'}`

    highlights.push({
      name: item.name,
      label: item.name,
      detectedPage: item.detectedPage || 0,
      coordinates,
    })
  })

  return highlights
}
